import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Pencil, Trash2, Stethoscope, Activity, Languages } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SymptomManagement = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('symptoms');
  const [symptoms, setSymptoms] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editLanguage, setEditLanguage] = useState('en');
  
  // Symptom form with both languages
  const [symptomForm, setSymptomForm] = useState({
    name: '',
    name_pt: '',
    description: '',
    description_pt: '',
    category: '',
    category_pt: ''
  });
  
  // Condition form with both languages
  const [conditionForm, setConditionForm] = useState({
    name: '',
    name_pt: '',
    description: '',
    description_pt: '',
    severity: 'moderate',
    recommendations: '',
    recommendations_pt: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [symptomsRes, conditionsRes] = await Promise.all([
        axios.get(`${API}/symptoms`),
        axios.get(`${API}/conditions`)
      ]);
      setSymptoms(symptomsRes.data);
      setConditions(conditionsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForms = () => {
    setSymptomForm({ name: '', name_pt: '', description: '', description_pt: '', category: '', category_pt: '' });
    setConditionForm({ name: '', name_pt: '', description: '', description_pt: '', severity: 'moderate', recommendations: '', recommendations_pt: '' });
    setEditingItem(null);
    setEditLanguage('en');
  };

  const handleOpenDialog = (item = null, type = activeTab) => {
    if (item) {
      setEditingItem(item);
      if (type === 'symptoms') {
        setSymptomForm({
          name: item.name || '',
          name_pt: item.name_pt || '',
          description: item.description || '',
          description_pt: item.description_pt || '',
          category: item.category || '',
          category_pt: item.category_pt || ''
        });
      } else {
        setConditionForm({
          name: item.name || '',
          name_pt: item.name_pt || '',
          description: item.description || '',
          description_pt: item.description_pt || '',
          severity: item.severity || 'moderate',
          recommendations: item.recommendations || '',
          recommendations_pt: item.recommendations_pt || ''
        });
      }
    } else {
      resetForms();
    }
    setIsOpen(true);
  };

  const handleSubmitSymptom = async (e) => {
    e.preventDefault();
    
    // Validate at least one language has name
    if (!symptomForm.name && !symptomForm.name_pt) {
      toast.error('Please enter a name in at least one language');
      return;
    }
    
    try {
      if (editingItem) {
        await axios.put(`${API}/symptoms/${editingItem.id}`, symptomForm);
        toast.success(t('update') + '!');
      } else {
        await axios.post(`${API}/symptoms`, symptomForm);
        toast.success(t('create') + '!');
      }
      fetchData();
      setIsOpen(false);
      resetForms();
    } catch (error) {
      toast.error('Failed to save symptom: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSubmitCondition = async (e) => {
    e.preventDefault();
    
    // Validate at least one language has name
    if (!conditionForm.name && !conditionForm.name_pt) {
      toast.error('Please enter a name in at least one language');
      return;
    }
    
    try {
      if (editingItem) {
        await axios.put(`${API}/conditions/${editingItem.id}`, conditionForm);
        toast.success(t('update') + '!');
      } else {
        await axios.post(`${API}/conditions`, conditionForm);
        toast.success(t('create') + '!');
      }
      fetchData();
      setIsOpen(false);
      resetForms();
    } catch (error) {
      toast.error('Failed to save condition: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Delete this ${type === 'symptoms' ? 'symptom' : 'condition'}?`)) return;
    try {
      await axios.delete(`${API}/${type}/${id}`);
      toast.success(t('delete') + '!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'mild': return 'severity-mild';
      case 'moderate': return 'severity-moderate';
      case 'severe': return 'severity-severe';
      default: return '';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'mild': return t('mild');
      case 'moderate': return t('moderate');
      case 'severe': return t('severe');
      default: return severity;
    }
  };

  // Get translated display values
  const getSymptomName = (symptom) => {
    if (language === 'pt' && symptom.name_pt) return symptom.name_pt;
    return symptom.name;
  };

  const getSymptomCategory = (symptom) => {
    if (language === 'pt' && symptom.category_pt) return symptom.category_pt;
    return symptom.category;
  };

  const getConditionName = (condition) => {
    if (language === 'pt' && condition.name_pt) return condition.name_pt;
    return condition.name;
  };

  const getConditionDescription = (condition) => {
    if (language === 'pt' && condition.description_pt) return condition.description_pt;
    return condition.description;
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="symptom-management">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-3 mb-2">
          <Stethoscope className="w-6 h-6 text-primary" />
          <h1 className="text-3xl font-bold">{t('symptomsAndConditions')}</h1>
        </div>
        <p className="text-muted-foreground mb-6">{t('manageSymptomsAndConditions')}</p>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-6">
            <TabsList className="rounded-full">
              <TabsTrigger value="symptoms" className="rounded-full" data-testid="tab-symptoms">
                {t('symptoms')}
              </TabsTrigger>
              <TabsTrigger value="conditions" className="rounded-full" data-testid="tab-conditions">
                {t('conditions')}
              </TabsTrigger>
            </TabsList>

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full" onClick={() => handleOpenDialog()} data-testid="add-item-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {activeTab === 'symptoms' ? t('addSymptom') : t('addCondition')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {editingItem 
                      ? (activeTab === 'symptoms' ? t('editSymptom') : t('editCondition'))
                      : (activeTab === 'symptoms' ? t('newSymptom') : t('newCondition'))}
                    <Languages className="w-4 h-4 text-muted-foreground" />
                  </DialogTitle>
                </DialogHeader>

                {activeTab === 'symptoms' ? (
                  <form onSubmit={handleSubmitSymptom} className="space-y-4 mt-4">
                    {/* Language tabs for symptom */}
                    <Tabs value={editLanguage} onValueChange={setEditLanguage} className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="en" className="flex items-center gap-2">
                          🇬🇧 English
                        </TabsTrigger>
                        <TabsTrigger value="pt" className="flex items-center gap-2">
                          🇵🇹 Português
                        </TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="en" className="mt-4 space-y-4">
                        <div className="space-y-2">
                          <Label>{t('symptomName')} (English)</Label>
                          <Input
                            value={symptomForm.name}
                            onChange={(e) => setSymptomForm({ ...symptomForm, name: e.target.value })}
                            placeholder="e.g., Headache"
                            data-testid="symptom-name-en"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('category')} (English)</Label>
                          <Input
                            value={symptomForm.category}
                            onChange={(e) => setSymptomForm({ ...symptomForm, category: e.target.value })}
                            placeholder="e.g., Neurological"
                            data-testid="symptom-category-en"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('description')} (English)</Label>
                          <Textarea
                            value={symptomForm.description}
                            onChange={(e) => setSymptomForm({ ...symptomForm, description: e.target.value })}
                            placeholder="Brief description..."
                            data-testid="symptom-description-en"
                          />
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="pt" className="mt-4 space-y-4">
                        <div className="space-y-2">
                          <Label>{t('symptomName')} (Português)</Label>
                          <Input
                            value={symptomForm.name_pt}
                            onChange={(e) => setSymptomForm({ ...symptomForm, name_pt: e.target.value })}
                            placeholder="ex: Dor de Cabeça"
                            data-testid="symptom-name-pt"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('category')} (Português)</Label>
                          <Input
                            value={symptomForm.category_pt}
                            onChange={(e) => setSymptomForm({ ...symptomForm, category_pt: e.target.value })}
                            placeholder="ex: Neurológico"
                            data-testid="symptom-category-pt"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('description')} (Português)</Label>
                          <Textarea
                            value={symptomForm.description_pt}
                            onChange={(e) => setSymptomForm({ ...symptomForm, description_pt: e.target.value })}
                            placeholder="Descrição breve..."
                            data-testid="symptom-description-pt"
                          />
                        </div>
                      </TabsContent>
                    </Tabs>

                    {/* Preview of both languages */}
                    {editingItem && (
                      <div className="p-3 bg-muted rounded-xl text-sm space-y-1">
                        <p><strong>EN:</strong> {symptomForm.name || <span className="text-muted-foreground italic">Not set</span>}</p>
                        <p><strong>PT:</strong> {symptomForm.name_pt || <span className="text-muted-foreground italic">Not set</span>}</p>
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setIsOpen(false)}>
                        {t('cancel')}
                      </Button>
                      <Button type="submit" className="flex-1 rounded-full" data-testid="save-symptom-btn">
                        {t('save')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleSubmitCondition} className="space-y-4 mt-4">
                    {/* Language tabs for condition */}
                    <Tabs value={editLanguage} onValueChange={setEditLanguage} className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="en" className="flex items-center gap-2">
                          🇬🇧 English
                        </TabsTrigger>
                        <TabsTrigger value="pt" className="flex items-center gap-2">
                          🇵🇹 Português
                        </TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="en" className="mt-4 space-y-4">
                        <div className="space-y-2">
                          <Label>{t('conditionName')} (English)</Label>
                          <Input
                            value={conditionForm.name}
                            onChange={(e) => setConditionForm({ ...conditionForm, name: e.target.value })}
                            placeholder="e.g., Common Cold"
                            data-testid="condition-name-en"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('description')} (English)</Label>
                          <Textarea
                            value={conditionForm.description}
                            onChange={(e) => setConditionForm({ ...conditionForm, description: e.target.value })}
                            placeholder="Brief description..."
                            data-testid="condition-description-en"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('recommendations')} (English)</Label>
                          <Textarea
                            value={conditionForm.recommendations}
                            onChange={(e) => setConditionForm({ ...conditionForm, recommendations: e.target.value })}
                            placeholder="Treatment recommendations..."
                            data-testid="condition-recommendations-en"
                          />
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="pt" className="mt-4 space-y-4">
                        <div className="space-y-2">
                          <Label>{t('conditionName')} (Português)</Label>
                          <Input
                            value={conditionForm.name_pt}
                            onChange={(e) => setConditionForm({ ...conditionForm, name_pt: e.target.value })}
                            placeholder="ex: Constipação"
                            data-testid="condition-name-pt"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('description')} (Português)</Label>
                          <Textarea
                            value={conditionForm.description_pt}
                            onChange={(e) => setConditionForm({ ...conditionForm, description_pt: e.target.value })}
                            placeholder="Descrição breve..."
                            data-testid="condition-description-pt"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('recommendations')} (Português)</Label>
                          <Textarea
                            value={conditionForm.recommendations_pt}
                            onChange={(e) => setConditionForm({ ...conditionForm, recommendations_pt: e.target.value })}
                            placeholder="Recomendações de tratamento..."
                            data-testid="condition-recommendations-pt"
                          />
                        </div>
                      </TabsContent>
                    </Tabs>

                    {/* Severity selector (same for both languages) */}
                    <div className="space-y-2">
                      <Label>{t('severity')}</Label>
                      <Select value={conditionForm.severity} onValueChange={(v) => setConditionForm({ ...conditionForm, severity: v })}>
                        <SelectTrigger data-testid="condition-severity">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mild">{t('mild')}</SelectItem>
                          <SelectItem value="moderate">{t('moderate')}</SelectItem>
                          <SelectItem value="severe">{t('severe')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Preview of both languages */}
                    {editingItem && (
                      <div className="p-3 bg-muted rounded-xl text-sm space-y-1">
                        <p><strong>EN:</strong> {conditionForm.name || <span className="text-muted-foreground italic">Not set</span>}</p>
                        <p><strong>PT:</strong> {conditionForm.name_pt || <span className="text-muted-foreground italic">Not set</span>}</p>
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setIsOpen(false)}>
                        {t('cancel')}
                      </Button>
                      <Button type="submit" className="flex-1 rounded-full" data-testid="save-condition-btn">
                        {t('save')}
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>

          <TabsContent value="symptoms">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : symptoms.length > 0 ? (
              <div className="grid gap-3">
                {symptoms.map((symptom, index) => (
                  <motion.div
                    key={symptom.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <Card className="p-4 rounded-2xl" data-testid={`symptom-item-${index}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Activity className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{getSymptomName(symptom)}</h3>
                            {getSymptomCategory(symptom) && (
                              <span className="text-xs text-muted-foreground">{getSymptomCategory(symptom)}</span>
                            )}
                            {/* Translation status */}
                            <div className="flex gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${symptom.name ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                                🇬🇧 {symptom.name ? '✓' : '✗'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${symptom.name_pt ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                                🇵🇹 {symptom.name_pt ? '✓' : '✗'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-full"
                            onClick={() => handleOpenDialog(symptom, 'symptoms')}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-full text-destructive"
                            onClick={() => handleDelete(symptom.id, 'symptoms')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-8 rounded-2xl text-center">
                <Stethoscope className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{t('noSymptomsYet')}</h3>
                <p className="text-muted-foreground text-sm">{t('addFirstSymptom')}</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="conditions">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : conditions.length > 0 ? (
              <div className="grid gap-3">
                {conditions.map((condition, index) => (
                  <motion.div
                    key={condition.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <Card className="p-4 rounded-2xl" data-testid={`condition-item-${index}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{getConditionName(condition)}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityClass(condition.severity)}`}>
                              {getSeverityLabel(condition.severity)}
                            </span>
                          </div>
                          {getConditionDescription(condition) && (
                            <p className="text-sm text-muted-foreground mt-1">{getConditionDescription(condition)}</p>
                          )}
                          {/* Translation status */}
                          <div className="flex gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${condition.name ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                              🇬🇧 {condition.name ? '✓' : '✗'}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${condition.name_pt ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                              🇵🇹 {condition.name_pt ? '✓' : '✗'}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-full"
                            onClick={() => handleOpenDialog(condition, 'conditions')}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-full text-destructive"
                            onClick={() => handleDelete(condition.id, 'conditions')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-8 rounded-2xl text-center">
                <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{t('noConditionsYet')}</h3>
                <p className="text-muted-foreground text-sm">{t('addFirstCondition')}</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
};
